import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LayoutDashboard, Users, AlertCircle, Briefcase, LogOut, GraduationCap, ClipboardList, Settings, UserCog, Shield, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: 'School Dashboard',
    logo_url: null,
    has_logo: false
  });
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    fetchSchoolInfo();
    fetchPermissions();
  }, []);

  const fetchSchoolInfo = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/school-info`);
      setSchoolInfo(data);
    } catch (error) {
      console.error('Error fetching school info:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/permissions/my-permissions`, {
        withCredentials: true
      });
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Check if user can view a feature based on permissions
  const canView = (feature) => {
    // Admin always has access
    if (user?.role === 'admin') return true;
    
    // Check permissions for other roles
    if (permissions && permissions[feature]) {
      return permissions[feature].can_view;
    }
    
    return false;
  };

  const navItems = [
    { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, show: true },
    { path: '/dashboard/daily-attendance', label: 'Daily Report', icon: ClipboardList, show: user?.role !== 'student' },
    { path: '/dashboard/disciplinary', label: 'Disciplinary', icon: AlertCircle, show: true },
    { path: '/dashboard/jobs', label: 'Job Applications', icon: Briefcase, show: canView('job_applications') },
    { path: '/dashboard/documents', label: 'Documents', icon: FileText, show: true },
    { path: '/dashboard/users', label: 'User Management', icon: UserCog, show: user?.role === 'admin' },
    { path: '/dashboard/permissions', label: 'Role Permissions', icon: Shield, show: user?.role === 'admin' },
    { path: '/dashboard/settings', label: 'Settings', icon: Settings, show: true },
  ];

  const filteredNavItems = navItems.filter(item => item.show);

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      {/* Sidebar */}
      <aside className="w-72 bg-gradient-to-b from-[#0B1F3F] to-[#0A2852] flex flex-col shadow-2xl">
        <div className="p-8 border-b border-white/10">
          <div className="flex flex-col items-center gap-4">
            <div className="w-20 h-20 bg-gradient-to-br from-[#667EEA] to-[#764BA2] rounded-2xl flex items-center justify-center shadow-xl overflow-hidden">
              {schoolInfo.has_logo ? (
                <img
                  src={`${BACKEND_URL}${schoolInfo.logo_url}?t=${Date.now()}`}
                  alt={schoolInfo.school_name}
                  className="w-full h-full object-cover"
                  data-testid="school-logo"
                />
              ) : (
                <GraduationCap className="w-10 h-10 text-white" />
              )}
            </div>
            <div className="text-center">
              <h2 className="font-bold text-white text-xl" style={{ fontFamily: 'Manrope' }}>
                {schoolInfo.school_name}
              </h2>
              <p className="text-xs text-blue-200 uppercase tracking-wider mt-1">Digital Platform</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-6 space-y-2">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => navigate(item.path)}
                className={`sidebar-link w-full ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-6 border-t border-white/10">
          <div className="mb-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10">
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-xs text-blue-200">{user?.email}</p>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/10 hover:border-white/30"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
